// Lightweight, GDPR-friendly analytics tracker.
// - Does NOTHING until the user grants consent via the CookieConsent banner.
// - Generates a persistent session_id (rolls over after 30min inactivity).
// - Reports page-view start when the route changes, and a duration update on
//   visibility-hidden / pagehide using sendBeacon (so it survives navigation).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSENT_KEY = "lefteria-analytics-consent"; // "granted" | "denied"
const SESSION_KEY = "lefteria-analytics-session";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min inactivity → new session

// Paths excluded from public-visitor analytics. We do NOT want admin or auth
// pages (or the mobile PWA which serves a different audience) to inflate the
// public visitor statistics shown on the dashboard.
const EXCLUDED_PATH_PREFIXES = [
  "/admin",      // covers /admin, /admin/login, /admin/*
  "/app",        // the mobile PWA (its own audience)
  "/login",      // customer login
  "/register",   // customer register
  "/invite",     // admin invite-accept flow
];

const isTrackablePath = (path) => {
  if (!path) return false;
  return !EXCLUDED_PATH_PREFIXES.some(p => path === p || path.startsWith(`${p}/`));
};

const uid = () => (crypto?.randomUUID?.() || (Math.random().toString(36).slice(2) + Date.now().toString(36)));

export const getConsent = () => {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
};

export const setConsent = (value) => {
  try { localStorage.setItem(CONSENT_KEY, value); } catch {}
};

const getOrCreateSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.id && (Date.now() - (s.last || 0)) < SESSION_TIMEOUT_MS) {
        s.last = Date.now();
        localStorage.setItem(SESSION_KEY, JSON.stringify(s));
        return { id: s.id, isNew: false };
      }
    }
  } catch {}
  const id = uid();
  try { localStorage.setItem(SESSION_KEY, JSON.stringify({ id, last: Date.now() })); } catch {}
  return { id, isNew: true };
};

let _pageStart = 0;
let _currentPath = "";

const post = (payload, useBeacon = false) => {
  const url = `${API}/analytics/track`;
  const body = JSON.stringify(payload);
  if (useBeacon && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    } catch {}
  }
  // Best-effort fetch
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
};

export const trackPageView = (path) => {
  if (getConsent() !== "granted") return;
  if (!isTrackablePath(path)) return;  // Skip admin/login/app paths
  const session = getOrCreateSession();
  // Send a duration update for the previous page first
  if (_currentPath && _pageStart) {
    const ms = Date.now() - _pageStart;
    if (ms > 1000) {
      post({ session_id: session.id, path: _currentPath, duration_ms: ms, consent: true });
    }
  }
  _currentPath = path;
  _pageStart = Date.now();
  post({
    session_id: session.id,
    path,
    referrer: document.referrer || null,
    is_new_session: session.isNew,
    consent: true,
  });
};

// Flush duration on tab close / hide. Uses sendBeacon for reliability.
export const installLifecycleHooks = () => {
  if (typeof document === "undefined") return;
  const flush = () => {
    if (getConsent() !== "granted") return;
    if (!_currentPath || !_pageStart) return;
    if (!isTrackablePath(_currentPath)) return;
    const ms = Date.now() - _pageStart;
    if (ms < 1000) return;
    const session = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    if (!session?.id) return;
    post({ session_id: session.id, path: _currentPath, duration_ms: ms, consent: true }, true);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
};
